/**
 * Product matching quiz — kreator wieloetapowy.
 *
 * Markup: templates/quiz-page.php. Endpoint: /wp-json/hn/v1/quiz-match
 * (inc/quiz.php). URL endpointu localizowany jako window.hnQuiz.endpoint;
 * fallback do <link rel="https://api.w.org/">.
 */

const AUTO_ADVANCE_MS = 280;

function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
}

function escapeAttr(value) {
    return String(value == null ? '' : value).replace(/"/g, '&quot;');
}

function resolveEndpoint() {
    if (window.hnQuiz && window.hnQuiz.endpoint) {
        return window.hnQuiz.endpoint;
    }
    const apiLink = document.querySelector('link[rel="https://api.w.org/"]');
    const base = apiLink ? apiLink.href : '/wp-json/';
    return base.replace(/\/$/, '') + '/hn/v1/quiz-match';
}

function track(event, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event }, payload || {}));
}

export function initQuiz() {
    const root = document.querySelector('[data-hn-quiz]');
    if (!root) {
        return;
    }

    const steps = Array.from(root.querySelectorAll('.hn-quiz__step'));
    if (!steps.length) {
        return;
    }

    const stepsWrap   = root.querySelector('.hn-quiz__steps');
    const progress    = root.querySelector('[data-hn-quiz-progress]');
    const progressBox = progress ? progress.closest('.hn-quiz__progress') : null;
    const progressText = root.querySelector('[data-hn-quiz-progress-text]');
    const resultsBox  = root.querySelector('[data-hn-quiz-results]');
    const resultsGrid = root.querySelector('[data-hn-quiz-results-grid]');
    const resultsEmpty = root.querySelector('[data-hn-quiz-results-empty]');
    const resultsLead = root.querySelector('[data-hn-quiz-results-lead]');
    const resultsNote = root.querySelector('[data-hn-quiz-results-note]');
    const debugEl     = root.querySelector('[data-hn-quiz-debug]');

    const endpoint = resolveEndpoint();
    let current = 0;
    let started = false;
    let advanceTimer = null;

    function updateProgress() {
        if (progress) {
            progress.style.width = Math.round(((current + 1) / steps.length) * 100) + '%';
        }
        if (progressText) {
            progressText.textContent = (current + 1) + '/' + steps.length;
        }
    }

    // Przewijamy tylko gdy pytanie wypadło poza wygodny obszar — bez skoku przy starcie.
    function ensureVisible(el) {
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        if (top < 72 || top > window.innerHeight * 0.55) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    function showStep(index, opts) {
        const scroll = !opts || opts.scroll !== false;
        current = Math.max(0, Math.min(index, steps.length - 1));
        steps.forEach((step, i) => { step.hidden = i !== current; });
        updateProgress();
        if (scroll) {
            ensureVisible(steps[current].querySelector('.hn-quiz__legend'));
        }
    }

    function markStarted() {
        if (started) return;
        started = true;
        root.classList.add('is-started'); // zwija intro — pytanie ląduje wyżej na mobile
        track('hn_quiz_start', { quiz_steps: steps.length });
    }

    // Krok jest poprawny gdy: optional, albo zaznaczono ≥1 opcję.
    function stepValid(step) {
        if (step.dataset.optional === '1') {
            return true;
        }
        return step.querySelectorAll('input:checked').length > 0;
    }

    function flagError(step) {
        step.classList.add('is-error');
        window.setTimeout(() => step.classList.remove('is-error'), 400);
    }

    function collectCriteria() {
        // Quiz dog-only — pytanie "gatunek" usunięte z UI, ale endpoint REST wymaga
        // gatunek_pies do filtrowania (hard criterion). Zawsze dodajemy.
        const values = ['gatunek_pies'];
        steps.forEach((step) => {
            step.querySelectorAll('input:checked').forEach((input) => {
                if (input.value) {
                    values.push(input.value);
                }
            });
        });
        return values;
    }

    function goNext() {
        const step = steps[current];
        if (!stepValid(step)) {
            flagError(step);
            return;
        }
        track('hn_quiz_step', {
            quiz_step: current + 1,
            quiz_question: step.dataset.questionId,
            quiz_answers: Array.from(step.querySelectorAll('input:checked')).map((i) => i.value).join(','),
        });
        if (current === steps.length - 1) {
            submit();
        } else {
            showStep(current + 1);
        }
    }

    function renderResults(data) {
        const results = (data && Array.isArray(data.results)) ? data.results : [];

        if (debugEl && data && data.debug) {
            debugEl.textContent = JSON.stringify(data.debug, null, 2);
        }

        if (!results.length) {
            resultsGrid.innerHTML = '';
            if (resultsEmpty) resultsEmpty.hidden = false;
            if (resultsLead) resultsLead.textContent = '';
            if (resultsNote) resultsNote.hidden = true;
            track('hn_quiz_no_results', { quiz_criteria: collectCriteria().join(',') });
            return;
        }

        if (resultsEmpty) resultsEmpty.hidden = true;
        if (resultsLead) {
            resultsLead.textContent = results.length === 1
                ? 'Jedna karma pasuje najlepiej:'
                : 'Wybraliśmy ' + results.length + ' z ' + (data.total || results.length) + ' produktów, które pasują:';
        }
        if (resultsNote) {
            // `relaxed` = nie mamy nic dokładnie na ten wiek, pokazujemy najbliższe.
            resultsNote.hidden = !data.relaxed;
        }

        resultsGrid.innerHTML = results.map((product, index) => {
            const isTop = index === 0;
            const badge = isTop
                ? '<span class="hn-quiz__card-badge">Najlepszy wybór</span>'
                : '';
            const reason = product.reason
                ? '<div class="hn-quiz__card-reason">' + escapeHtml(product.reason) + '</div>'
                : '';
            // Klasy WC (add_to_cart_button / ajax_add_to_cart) — ten sam kontrakt co
            // kafelki w sklepie, więc side cart i GA4 działają bez osobnego kodu.
            const cta = product.add_to_cart
                ? '<a class="hn-btn hn-btn--primary hn-quiz__card-atc add_to_cart_button' +
                      (product.ajax_cart ? ' ajax_add_to_cart' : '') + '"' +
                      ' href="' + escapeAttr(product.add_to_cart) + '"' +
                      ' data-quantity="1" data-product_id="' + escapeAttr(product.id) + '"' +
                      ' data-hn-ga4=\'' + escapeAttr(JSON.stringify(product.ga4 || {})) + '\'' +
                      ' aria-label="Dodaj ' + escapeAttr(product.name) + ' do koszyka">Dodaj do koszyka</a>'
                : '<a class="hn-btn hn-btn--ghost hn-quiz__card-atc" href="' + escapeAttr(product.url) + '">Zobacz produkt →</a>';
            return (
                '<div class="hn-quiz__card' + (isTop ? ' hn-quiz__card--top' : '') + '"' +
                    ' data-hn-quiz-result="' + escapeAttr(product.id) + '" data-hn-quiz-result-pos="' + (index + 1) + '">' +
                    '<a class="hn-quiz__card-media" href="' + escapeAttr(product.url) + '" tabindex="-1" aria-hidden="true">' +
                        '<img class="hn-quiz__card-image" src="' + escapeAttr(product.image) + '" alt="" loading="lazy">' +
                    '</a>' +
                    '<div class="hn-quiz__card-body">' +
                        badge +
                        '<h3 class="hn-quiz__card-title"><a href="' + escapeAttr(product.url) + '"' +
                            ' title="' + escapeAttr(product.name) + '">' + escapeHtml(product.short_name || product.name) + '</a></h3>' +
                        reason +
                        '<div class="hn-quiz__card-price">' + (product.price_html || '') + '</div>' +
                        cta +
                    '</div>' +
                '</div>'
            );
        }).join('');

        track('hn_quiz_complete', {
            quiz_criteria: collectCriteria().join(','),
            quiz_results: results.length,
            quiz_total: data.total || results.length,
            quiz_relaxed: !!data.relaxed,
        });
    }

    async function submit() {
        const step = steps[current];
        if (!stepValid(step)) {
            flagError(step);
            return;
        }

        const criteria = collectCriteria();
        if (stepsWrap) stepsWrap.hidden = true;
        if (progressBox) progressBox.hidden = true;
        if (resultsBox) resultsBox.hidden = false;
        if (resultsEmpty) resultsEmpty.hidden = true;
        if (resultsNote) resultsNote.hidden = true;
        resultsGrid.innerHTML = '<div class="hn-quiz__loading">Dobieramy produkty dla Twojego pupila…</div>';
        ensureVisible(resultsBox);

        const url = new URL(endpoint, window.location.origin);
        criteria.forEach((value) => url.searchParams.append('criteria[]', value));
        url.searchParams.set('limit', '3');

        try {
            const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
            renderResults(await response.json());
        } catch (error) {
            resultsGrid.innerHTML = '';
            if (resultsEmpty) resultsEmpty.hidden = false;
        }
    }

    function restart() {
        root.querySelectorAll('.hn-quiz__step input:checked').forEach((input) => {
            input.checked = false;
        });
        if (resultsBox) resultsBox.hidden = true;
        if (stepsWrap) stepsWrap.hidden = false;
        if (progressBox) progressBox.hidden = false;
        track('hn_quiz_restart', {});
        showStep(0);
    }

    // Zaznaczenie odpowiedzi. Krok jednokrotnego wyboru sam przechodzi dalej —
    // oszczędza tap + scroll do "Dalej" na każdym pytaniu (główny koszt na mobile).
    root.addEventListener('change', (event) => {
        const input = event.target.closest('.hn-quiz__step input');
        if (!input) return;
        markStarted();
        const step = steps[current];
        if (step.dataset.multi === '1' || !input.checked) return;
        window.clearTimeout(advanceTimer);
        advanceTimer = window.setTimeout(goNext, AUTO_ADVANCE_MS);
    });

    // Delegacja — przyciski są statyczne w obrębie [data-hn-quiz].
    root.addEventListener('click', (event) => {
        const card = event.target.closest('[data-hn-quiz-result]');
        if (card) {
            track(event.target.closest('.hn-quiz__card-atc') ? 'hn_quiz_add_to_cart' : 'hn_quiz_result_click', {
                quiz_product_id: card.dataset.hnQuizResult,
                quiz_position: card.dataset.hnQuizResultPos,
            });
            return;
        }

        const target = event.target.closest(
            '[data-hn-quiz-next],[data-hn-quiz-back],[data-hn-quiz-skip],[data-hn-quiz-submit],[data-hn-quiz-restart]'
        );
        if (!target) {
            return;
        }

        window.clearTimeout(advanceTimer);

        if (target.hasAttribute('data-hn-quiz-next') || target.hasAttribute('data-hn-quiz-submit')) {
            markStarted();
            goNext();
        } else if (target.hasAttribute('data-hn-quiz-back')) {
            showStep(current - 1);
        } else if (target.hasAttribute('data-hn-quiz-skip')) {
            markStarted();
            track('hn_quiz_skip', { quiz_step: current + 1 });
            showStep(current + 1);
        } else if (target.hasAttribute('data-hn-quiz-restart')) {
            restart();
        }
    });

    showStep(0, { scroll: false });
}
